import axios from 'axios';
import { message } from 'antd';
import { useUserStore } from '@/stores/userStore';

export interface CabinCard {
  id: number;
  cabinId: string;
  name: string;
  desc: string | null;
  url: string | null;
  enabled: boolean;
  createdAt: string;
}

export interface CreateCabinCardPayload {
  cabinId: string;
  name: string;
  desc?: string;
  url?: string;
}

export interface UpdateCabinCardPayload {
  cabinId?: string;
  name?: string;
  desc?: string;
  url?: string;
  enabled?: boolean;
}

export interface CabinMember {
  id: string;
  username: string;
  email: string;
  realName?: string;
}

const apiClient = axios.create({
  baseURL: '/api/v1',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const userInfo = useUserStore.getState().userInfo;
  if (userInfo?.id) {
    config.headers = config.headers || {};
    (config.headers as any)['X-User-Identifier'] = userInfo.id;
  }
  return config;
});

apiClient.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const data = error?.response?.data;
    const status = error?.response?.status;
    if (status === 403) message.error(data?.message || '权限不足');
    else if (status >= 500) message.error('服务异常，请稍后再试');
    return Promise.reject(error);
  },
);

export const cabinConfigListEnabled = (): Promise<CabinCard[]> =>
  apiClient.get<CabinCard[]>('/cabin-configs').then((r) => r.data);

export const cabinConfigListAll = (): Promise<CabinCard[]> =>
  apiClient.get<CabinCard[]>('/cabin-configs/all').then((r) => r.data);

export const cabinConfigCreate = (payload: CreateCabinCardPayload): Promise<CabinCard> =>
  apiClient.post<CabinCard>('/cabin-configs', payload).then((r) => r.data);

export const cabinConfigToggle = (id: number, enabled: boolean): Promise<CabinCard> =>
  apiClient.patch<CabinCard>(`/cabin-configs/${id}`, { enabled }).then((r) => r.data);

export const cabinConfigUpdate = (id: number, payload: UpdateCabinCardPayload): Promise<CabinCard> =>
  apiClient.patch<CabinCard>(`/cabin-configs/${id}`, payload).then((r) => r.data);

export const cabinConfigDelete = (id: number): Promise<void> =>
  apiClient.delete<void>(`/cabin-configs/${id}`).then(() => undefined);

export const cabinConfigGetPermissions = (cardId: number): Promise<string[]> =>
  apiClient.get<string[]>(`/cabin-configs/${cardId}/permissions`).then((r) => r.data);

export const cabinConfigSetPermissions = (cardId: number, userIds: string[]): Promise<string[]> =>
  apiClient.put<string[]>(`/cabin-configs/${cardId}/permissions`, { userIds }).then((r) => r.data);

export const cabinConfigGetUserCardIds = (userId: string): Promise<number[]> =>
  apiClient.get<number[]>(`/cabin-configs/user/${userId}/card-ids`).then((r) => r.data);

export const cabinConfigListMembers = (): Promise<CabinMember[]> =>
  apiClient.get<CabinMember[]>('/cabin-configs/members').then((r) => r.data);
